import React from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Button from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';

export type OptionsHistoryRow = [string, string, string];

const PAINT_HISTORY_COLUMNS: OptionsHistoryRow = [
  'Contractor',
  'Requested Date',
  'Requested By',
];

// Row shape of a job-interior-exterior-options-history-report entry.
export const historyItemToRow = (item: any): OptionsHistoryRow => [
  item.jobPhase?.contractor?.name || 'N/A',
  item.date ? new Date(item.date).toLocaleDateString() : 'N/A',
  item.user ? `${item.user.firstName} ${item.user.lastName}` : 'N/A',
];

// Paint payout line: hidden while the report field is absent, "—" when the
// report yields NULL.
export const formatPaintPayoutAmount = (
  amount?: string | null,
): string | undefined => {
  if (amount === undefined) return undefined;
  return amount ? `$${amount}` : '—';
};

interface OptionsHistoryModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  rows: OptionsHistoryRow[];
  loading: boolean;
  columns?: OptionsHistoryRow;
  // Display-ready text; the payout line is omitted when undefined.
  payoutAmount?: string;
}

const OptionsHistoryModal: React.FC<OptionsHistoryModalProps> = ({
  open,
  onClose,
  title,
  rows,
  loading,
  columns = PAINT_HISTORY_COLUMNS,
  payoutAmount,
}) => {
  const payoutLine =
    payoutAmount !== undefined ? (
      <Typography variant="body2" style={{ fontStyle: 'italic' }}>
        Payout Amount: {payoutAmount}
      </Typography>
    ) : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h6">{title}</Typography>
        {payoutLine}
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <Typography>Loading...</Typography>
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                {columns.map(column => (
                  <TableCell key={column}>{column}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length > 0 ? (
                rows.map((row, index) => (
                  <TableRow key={index}>
                    {row.map((cell, cellIndex) => (
                      <TableCell key={cellIndex}>{cell}</TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    <Typography color="textSecondary">
                      No history available
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}

        <Box display="flex" justifyContent="center" mt={3} mb={2}>
          <Button onClick={onClose} variant="contained" color="primary">
            CLOSE
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default OptionsHistoryModal;
